import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { NavLogo } from "./Logo";
import { useLang } from "../i18n";
import { PRODUCT_LINKS } from "../productLinks";
import { SOLUTIONS } from "../solutionsData";
import { trackGA4Event } from "../analytics/ga4";

const T = (lang, v) => (v && typeof v === "object" ? v[lang] || v.en : v);

const LINKS = [
    { to: "/about", label: { en: "About", hi: "परिचय" }, id: "nav-link-about" },
    { to: "/installers", label: { en: "Installers", hi: "इंस्टॉलर" }, id: "nav-link-installers" },
    { to: "/gallery", label: { en: "Gallery", hi: "गैलरी" }, id: "nav-link-gallery" },
    { to: "/warranty", label: { en: "Warranty", hi: "वारंटी" }, id: "nav-link-warranty" },
    { to: "/contact", label: { en: "Contact", hi: "संपर्क" }, id: "nav-link-contact" },
];

const linkClass = ({ isActive }) =>
    `font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:text-brand ${
        isActive ? "text-brand" : "text-zinc-300"
    }`;

function Dropdown({ id, label, items, indexTo, indexLabel, lang }) {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const active = pathname.startsWith(indexTo);
    return (
        <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <Link
                to={indexTo}
                data-testid={id}
                aria-haspopup="true"
                aria-expanded={open}
                className={`flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:text-brand ${active ? "text-brand" : "text-zinc-300"}`}
            >
                {label}
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </Link>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid={`${id}-menu`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-1/2 top-full z-50 w-[340px] -translate-x-1/2 pt-4"
                    >
                        <div className="clip-notch border border-white/10 bg-navy/95 p-2 shadow-[0_30px_80px_rgba(0,0,0,0.5)] backdrop-blur-md">
                            {items.map((it) => (
                                <Link
                                    key={it.to}
                                    to={it.to}
                                    data-testid={`${id}-item-${it.to.split("/").pop()}`}
                                    onClick={() => setOpen(false)}
                                    className="group flex items-center justify-between gap-3 px-3 py-2.5 transition-colors duration-200 hover:bg-white/5"
                                >
                                    <span className="flex items-center gap-3">
                                        <span className="font-mono text-[10px] tracking-[0.15em] text-brand">{it.idx}</span>
                                        <span className="text-sm text-paper">{T(lang, it.title)}</span>
                                    </span>
                                    <span className="font-mono text-[10px] text-zinc-600 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-brand">→</span>
                                </Link>
                            ))}
                            <Link
                                to={indexTo}
                                onClick={() => setOpen(false)}
                                className="mt-1 block border-t border-white/10 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-200 hover:text-brand"
                            >
                                {indexLabel} →
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function MobileMenu({ open, onClose, lang }) {
    const group = (title, items, testid) => (
        <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">{title}</p>
            <div className="mt-3 grid gap-2">
                {items.map((it) => (
                    <Link key={it.to} to={it.to} onClick={onClose} data-testid={`${testid}-${it.to.split("/").pop()}`} className="flex items-center gap-3 border-b border-white/5 py-2 text-base text-paper">
                        {it.idx && <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-500">{it.idx}</span>}
                        {T(lang, it.title || it.label)}
                    </Link>
                ))}
            </div>
        </div>
    );
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="mobile-menu"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[60] overflow-y-auto bg-navy/97 backdrop-blur-md xl:hidden"
                >
                    <div className="flex items-center justify-between px-5 py-4">
                        <Link to="/" onClick={onClose} aria-label="Home"><NavLogo /></Link>
                        <button data-testid="mobile-menu-close" onClick={onClose} aria-label="Close menu" className="flex h-11 w-11 items-center justify-center border border-white/20 text-paper">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <motion.div
                        initial={{ y: 24, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.45, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                        className="grid gap-10 px-5 pb-16 pt-6 md:grid-cols-2"
                    >
                        {group(lang === "hi" ? "उत्पाद" : "Products", [...PRODUCT_LINKS.map((p) => ({ to: `/products/${p.slug}`, idx: p.idx, title: p.title })), { to: "/products", title: { en: "All products", hi: "सभी उत्पाद" } }], "mobile-product")}
                        {group(lang === "hi" ? "समाधान" : "Solutions", [...SOLUTIONS.map((s) => ({ to: `/solutions/${s.slug}`, idx: s.idx, title: s.title })), { to: "/solutions", title: { en: "All solutions", hi: "सभी समाधान" } }], "mobile-solution")}
                        {group(lang === "hi" ? "कंपनी" : "Company", [{ to: "/", label: { en: "Home", hi: "होम" } }, ...LINKS, { to: "/faq", label: { en: "FAQ", hi: "सवाल-जवाब" } }], "mobile-link")}
                        <div className="self-end">
                            <Link to="/contact" onClick={() => { trackGA4Event("generate_lead", { cta_location: "mobile_nav" }); onClose(); }} data-testid="mobile-cta-quote" className="inline-flex w-full items-center justify-center bg-brand px-7 py-4 text-sm font-semibold uppercase tracking-wider text-ink">
                                {lang === "hi" ? "कोटेशन पाएं" : "Get a quote"}
                            </Link>
                            <a href="https://wa.me/919782207225" onClick={() => trackGA4Event("whatsapp_click", { cta_location: "mobile_nav" })} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex w-full items-center justify-center border border-white/25 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper">
                                WhatsApp +91 97822 07225
                            </a>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [menu, setMenu] = useState(false);
    const { lang, setLang } = useLang();
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setMenu(false), [pathname]);
    useEffect(() => {
        document.body.style.overflow = menu ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menu]);

    const solid = scrolled || pathname !== "/";

    return (
        <header
            data-testid="header-navigation-bar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                solid ? "border-b border-white/10 bg-navy/90 backdrop-blur-md" : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-3 px-5 py-4 md:px-10">
                <Link to="/" data-testid="nav-brand-logo" aria-label="DESAMATIC AUTOMATION home" className="shrink-0">
                    <NavLogo />
                </Link>
                <nav className="hidden items-center gap-7 xl:flex" aria-label="Main">
                    <Dropdown
                        id="nav-link-products"
                        lang={lang}
                        label={lang === "hi" ? "उत्पाद" : "Products"}
                        indexTo="/products"
                        indexLabel={lang === "hi" ? "सभी उत्पाद" : "All products"}
                        items={PRODUCT_LINKS.map((p) => ({ to: `/products/${p.slug}`, idx: p.idx, title: p.title }))}
                    />
                    <Dropdown
                        id="nav-link-solutions"
                        lang={lang}
                        label={lang === "hi" ? "समाधान" : "Solutions"}
                        indexTo="/solutions"
                        indexLabel={lang === "hi" ? "सभी समाधान" : "All solutions"}
                        items={SOLUTIONS.map((s) => ({ to: `/solutions/${s.slug}`, idx: s.idx, title: s.title }))}
                    />
                    {LINKS.map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={l.id} className={linkClass}>
                            {l.label[lang]}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <div data-testid="lang-toggle" className="flex border border-white/20">
                        <button
                            data-testid="lang-toggle-en"
                            onClick={() => setLang("en")}
                            className={`px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${lang === "en" ? "bg-brand text-ink" : "text-zinc-400 hover:text-paper"}`}
                        >
                            EN
                        </button>
                        <button
                            data-testid="lang-toggle-hi"
                            onClick={() => setLang("hi")}
                            className={`px-2.5 py-1.5 text-sm transition-colors duration-300 ${lang === "hi" ? "bg-brand text-ink" : "text-zinc-400 hover:text-paper"}`}
                        >
                            हिं
                        </button>
                    </div>
                    <Link
                        data-testid="nav-cta-get-quote"
                        to="/contact"
                        onClick={() => trackGA4Event("generate_lead", { cta_location: "desktop_nav" })}
                        className="hidden border border-brand/60 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:bg-brand hover:text-ink sm:inline-block"
                    >
                        {lang === "hi" ? "कोटेशन पाएं" : "Get a quote"}
                    </Link>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setMenu(true)}
                        aria-label="Open menu"
                        className="flex h-10 w-10 items-center justify-center border border-white/20 text-paper transition-colors duration-300 hover:border-brand hover:text-brand xl:hidden"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </div>
            <MobileMenu open={menu} onClose={() => setMenu(false)} lang={lang} />
        </header>
    );
}
